import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

// Create motion wrapper components
const MotionBox = motion(Box);

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '80vh', md: '90vh' },
          background: `linear-gradient(135deg, rgba(10, 14, 20, 0.9) 0%, rgba(255, 215, 0, 0.2) 50%, rgba(184, 149, 10, 0.2) 100%), url('/images/BearsBulls.jpeg') center/cover no-repeat`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(10, 14, 20, 0.85) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(10, 14, 20, 0.85) 100%)',
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, px: { xs: 2, sm: 3 } }}>
          <MotionBox
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            sx={{
              textAlign: 'center',
              py: { xs: 3, sm: 4, md: 8 },
              px: { xs: 1, sm: 2 },
            }}
          >
            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                component="h1"
                gutterBottom
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  mb: 3,
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
                  filter: 'drop-shadow(0 2px 10px rgba(255, 215, 0, 0.3))',
                }}
              >
                Trade Smarter, Not Harder
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                component="h2"
                sx={{ 
                  mb: 4, 
                  maxWidth: '800px', 
                  mx: 'auto',
                  color: '#FFFFFF',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.9), 0 2px 10px rgba(0, 0, 0, 0.8)',
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                  lineHeight: 1.4,
                }}
              >
                Join the elite with cutting-edge Expert Advisors, an exclusive trading community, 
                and premium synthetics signals that deliver consistent profits.
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1.5, sm: 2 }, 
                justifyContent: 'center', 
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                px: { xs: 2, sm: 0 },
              }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: '#000000',
                    fontWeight: 700,
                    boxShadow: '0 8px 25px rgba(255, 215, 0, 0.3)',
                    minWidth: { xs: '200px', sm: 'auto' },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '300px', sm: 'none' },
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, #996F08)`,
                      boxShadow: '0 12px 35px rgba(255, 215, 0, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Get Started Today
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/about')}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    backdropFilter: 'blur(10px)',
                    fontWeight: 600,
                    minWidth: { xs: '200px', sm: 'auto' },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '300px', sm: 'none' },
                    '&:hover': {
                      borderColor: 'primary.light',
                      backgroundColor: 'rgba(255, 215, 0, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </motion.div>
          </MotionBox>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg">
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          sx={{
            textAlign: 'center',
            py: { xs: 6, md: 10 },
          }}
        >
          <motion.div variants={itemVariants}>
            <Typography
              variant={isMobile ? 'h4' : 'h2'}
              component="h2"
              gutterBottom
              sx={{ mb: 3 }}
            >
              Ready to Join T1 FX Team?
            </Typography>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}
            >
              Access our Expert Advisors, join our trading community, and get premium synthetics signals for Deriv and Weltrade.
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: '#000000',
                fontWeight: 700,
                boxShadow: '0 8px 25px rgba(255, 215, 0, 0.3)',
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, #996F08)`,
                  boxShadow: '0 12px 35px rgba(255, 215, 0, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Get Started Today
            </Button>
          </motion.div>
        </MotionBox>
      </Container>
    </Box>
  );
}; 